import { useQuery } from '@tanstack/react-query';
import {
  Action,
  Badge,
  Card,
  Feed,
  Icon,
  Page,
  PageStatus,
  RelativeDateTime,
} from 'corso-admin-ui';
import { type CommandAdminApi } from 'corso-types';
import { formatter } from 'corso-web-lib';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import api from '~/api';
import { DataListCard } from '~/components/DataList';
import { usePathParams } from '~/hooks/usePathParams';

type Conversation = CommandAdminApi.ResponseBody<
  '/sidekick/conversation/:conversationId',
  'get'
>;

const nameOf = ({ firstName, lastName, email }: Conversation['user']) =>
  [firstName, lastName].filter(Boolean).join(' ') || email;

const sentimentVariant = {
  Positive: 'success',
  Neutral: 'default',
  Negative: 'danger',
} as const;

function MessageThread({ conversation }: { conversation: Conversation }) {
  const asker = nameOf(conversation.user);
  return (
    <Card>
      <Card.Heading>Conversation</Card.Heading>
      <Feed
        events={conversation.messages.map((message) => ({
          ...message,
          iconSize: '2rem',
        }))}
        renderIcon={(message) =>
          message.role === 'User' ? (
            <div className="flex size-8 items-center justify-center rounded-full border bg-white text-xs font-bold text-neutral-500">
              {asker.charAt(0).toUpperCase()}
            </div>
          ) : (
            <div className="flex size-8 items-center justify-center rounded-full border bg-white text-corso-blue-600">
              <Icon icon="ai" />
            </div>
          )
        }
      >
        {(message) => (
          <div className="w-full">
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-t-md border p-2 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold">
                  {message.role === 'User' ? asker : 'Sidekick'}
                </span>
                {message.toolCalls.map((tool, index) => (
                  // a tool can run more than once in an answer
                  // oxlint-disable-next-line react/no-array-index-key
                  <Badge key={`${tool}-${index}`}>{tool}</Badge>
                ))}
                {message.pageContext && (
                  <Badge variant="info">
                    {message.pageContext.kind} · {message.pageContext.title}
                  </Badge>
                )}
                {message.failed && <Badge variant="danger">Failed</Badge>}
                {message.userSentiment && (
                  <Badge variant={sentimentVariant[message.userSentiment]}>
                    {message.userSentiment}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-neutral-500">
                {message.usage && (
                  <span>
                    {message.usage.model} · {message.usage.effort} effort ·{' '}
                    {formatter.count(
                      message.usage.inputTokens +
                        message.usage.cacheReadTokens +
                        message.usage.cacheWriteTokens,
                      true,
                    )}{' '}
                    in / {formatter.count(message.usage.outputTokens, true)} out
                  </span>
                )}
                {message.role === 'Assistant' && (
                  <span className="font-medium text-neutral-800">
                    {message.costUsd === null
                      ? 'Unpriced'
                      : formatter.currency(message.costUsd, 'USD', false, {
                          maximumFractionDigits: 4,
                        })}
                  </span>
                )}
                <RelativeDateTime dateTime={message.createdOn} />
              </div>
            </div>
            <div className="rounded-b-md border-x border-b p-3 text-sm whitespace-pre-wrap text-neutral-800">
              {message.content}
              {message.userComment && (
                <p className="mt-2 border-t pt-2 text-xs text-neutral-500">
                  Feedback: {message.userComment}
                </p>
              )}
            </div>
          </div>
        )}
      </Feed>
    </Card>
  );
}

export default function SidekickConversationPage() {
  const { conversationId } = usePathParams(
    z.object({ conversationId: z.coerce.number() }),
  );

  const query = useQuery({
    queryKey: ['sidekick-conversation', conversationId],
    queryFn: () => api.sidekick.getConversation(conversationId),
  });

  return (
    <Page
      size="fullWidth"
      title={query.data?.name ?? 'Conversation'}
      backAction={{
        to: '/sidekick',
        accessibilityLabel: 'Back to conversations',
      }}
    >
      {match(query)
        .with({ status: 'pending' }, () => (
          <PageStatus.Loading title="Loading conversation" />
        ))
        .with({ status: 'error' }, () => (
          <PageStatus.Error
            title="Failed to load conversation"
            error={query.error}
          />
        ))
        .with({ status: 'success', data: P.select() }, (conversation) => (
          <div className="flex flex-col gap-4">
            <DataListCard
              items={[
                {
                  label: 'Store',
                  value: (
                    <Action
                      variant="link"
                      to={`/stores/${conversation.store.id}`}
                    >
                      {conversation.store.name}
                    </Action>
                  ),
                },
                {
                  label: 'User',
                  value: (
                    <Action
                      variant="link"
                      to={`/users/${conversation.user.id}`}
                    >
                      {nameOf(conversation.user)}
                    </Action>
                  ),
                },
                {
                  label: 'Started',
                  value: (
                    <RelativeDateTime dateTime={conversation.createdOn} full />
                  ),
                },
                {
                  label: 'Cost',
                  value: formatter.currency(
                    conversation.costUsd,
                    'USD',
                    false,
                    {
                      maximumFractionDigits: 4,
                    },
                  ),
                },
              ]}
            />
            <MessageThread conversation={conversation} />
          </div>
        ))
        .exhaustive()}
    </Page>
  );
}
